import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      setCharCount(0);
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch(
        'https://readdy.ai/api/form/d9q2gesnlsngrm4llqeg',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        }
      );
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      if (response.ok && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        const serverMsg =
          (parsed?.meta as Record<string, string>)?.message ||
          (parsed?.meta as Record<string, string>)?.detail ||
          (parsed?.message as string) ||
          responseText ||
          '提交失败';
        setFormError(String(serverMsg));
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const milestones = [
    { year: '2018', title: '平台创立', desc: '爱液下载平台正式上线，专注提供安全可靠的软件资源下载服务' },
    { year: '2019', title: '资源破千', desc: '平台收录资源突破1000款，覆盖5大主流分类，月活用户突破50万' },
    { year: '2020', title: '安全升级', desc: '引入AI安全检测系统，实现100%上线前安全扫描，通过率行业领先' },
    { year: '2022', title: '用户500万', desc: '累计服务用户突破500万，成为国内知名的资源下载平台之一' },
    { year: '2024', title: 'AI赋能', desc: '上线AI智能推荐系统，根据用户偏好精准匹配资源，提升下载体验' },
    { year: '2026', title: '全新升级', desc: '平台全面焕新，新增8大资源分类，资源总数突破2000+，持续为用户创造价值' },
  ];

  const advantages = [
    {
      icon: 'ri-shield-check-line',
      title: '安全可靠',
      desc: '所有资源经过严格安全检测，确保无病毒、无恶意插件、无捆绑软件，让您下载无忧',
    },
    {
      icon: 'ri-flashlight-line',
      title: '极速下载',
      desc: '多线路CDN加速，智能选择最优节点，确保高速稳定的下载体验，大文件也能快速完成',
    },
    {
      icon: 'ri-smartphone-line',
      title: '多平台兼容',
      desc: '支持Windows、macOS、Linux、Android、iOS等全平台，一处下载，多端使用',
    },
    {
      icon: 'ri-user-heart-line',
      title: '用户至上',
      desc: '以用户需求为导向，持续优化产品体验，7x24小时客服支持，用心服务每一位用户',
    },
    {
      icon: 'ri-refresh-line',
      title: '持续更新',
      desc: '资源库实时更新，确保您获取的始终是最新版本，紧跟软件迭代步伐',
    },
    {
      icon: 'ri-price-tag-3-line',
      title: '免费畅享',
      desc: '平台大量资源免费提供，让优质软件触手可及，降低您的软件使用成本',
    },
  ];

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于爱液下载',
    description: '爱液下载是一个安全、快速、免费的资源下载平台，致力于为用户提供优质的软件下载服务。',
    about: {
      '@type': 'Organization',
      name: '爱液下载',
      foundingDate: '2018',
      description: '安全快速免费的资源下载平台',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+86-400-888-9999',
        contactType: 'customer service',
        email: 'support@aiyexiazai.com',
      },
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-14 md:py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
              关于爱液下载
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              爱液下载成立于2018年，是一家专注于软件资源下载服务的平台。我们致力于为用户提供
              <strong className="text-foreground-900">安全、快速、免费</strong>的软件下载体验，
              帮助每一位用户轻松获取所需的数字工具和资源。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">发展历程</h2>
              <p className="text-sm text-foreground-600">从创立至今，我们一直在路上</p>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px"></div>
              <div className="space-y-8">
                {milestones.map((m, index) => (
                  <div key={m.year} className={`relative flex flex-col md:flex-row gap-4 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="md:w-1/2 flex items-center gap-4 pl-10 md:pl-0">
                      <div className={`md:w-full ${index % 2 === 0 ? 'md:text-right md:pr-10' : 'md:text-left md:pl-10'}`}>
                        <span className="inline-block text-xs font-bold text-primary-600 bg-primary-100 px-3 py-1 rounded-full mb-2">
                          {m.year}
                        </span>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1">{m.title}</h3>
                        <p className="text-xs text-foreground-500 leading-relaxed">{m.desc}</p>
                      </div>
                    </div>
                    <div className="absolute left-[13px] md:left-1/2 top-1.5 w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50 md:-translate-x-1/2"></div>
                    <div className="md:w-1/2 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">为什么选择我们</h2>
              <p className="text-sm text-foreground-600">六大核心优势，打造极致下载体验</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {advantages.map((adv) => (
                <div key={adv.title} className="p-6 bg-background-50 border border-background-200/70 rounded-xl text-center hover:border-accent-300 transition-all">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className={`${adv.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">{adv.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{adv.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-100">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">联系我们</h2>
              <p className="text-sm text-foreground-600">有任何问题或建议？欢迎给我们留言</p>
            </div>

            {formStatus === 'success' ? (
              <div className="text-center py-10 bg-background-50 border border-accent-200 rounded-xl">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">感谢您的留言！</h3>
                <p className="text-sm text-foreground-500">我们已收到您的信息，客服团队会在24小时内回复您。</p>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="mt-4 text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
                >
                  继续留言
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="space-y-5 bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
                {formStatus === 'error' && (
                  <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                    {formError}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    placeholder="请输入留言主题"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 resize-none"
                    placeholder="请输入您的留言内容（限500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">{charCount}/500</p>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />

                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  提交留言
                </button>
              </form>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-10">
              <div className="text-center p-5 bg-background-50 border border-background-200/70 rounded-xl">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">邮箱</h4>
                <p className="text-xs text-foreground-500">support@aiyexiazai.com</p>
              </div>
              <div className="text-center p-5 bg-background-50 border border-background-200/70 rounded-xl">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">电话</h4>
                <p className="text-xs text-foreground-500">400-888-9999</p>
              </div>
              <div className="text-center p-5 bg-background-50 border border-background-200/70 rounded-xl">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-3">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">地址</h4>
                <p className="text-xs text-foreground-500">北京市海淀区中关村科技园</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}